import React, { useEffect, useMemo } from "react"
import {
  SectionCard,
  planInputStyle as inputStyle,
  planTextareaStyle as textareaStyle,
  planButtonStyle as buttonStyle,
  planLabelStyle as labelStyle,
  TreatmentDraftCard
} from "./PlanPrimitives"

export default function PlanDraftBuilder({
  createModeOpen,
  onClose,
  patientIdFromUrl,
  selectedPatientId,
  selectedPatientName,
  loadingPatients,
  patientSearch,
  setPatientSearch,
  patients,
  setSelectedPatientId,
  setSelectedPatientName,
  planHeader,
  setPlanHeader,
  planDraftTotal,
  formatMoney,
  showQuickTreatmentForm,
  setShowQuickTreatmentForm,
  quickTreatmentForm,
  setQuickTreatmentForm,
  handleQuickCreateTreatment,
  savingQuickTreatment,
  itemDraft,
  setItemDraft,
  loadingTreatments,
  treatmentsSorted,
  addDraftItem,
  planItems,
  removeDraftItem,
  createManualPlan,
  creatingPlan,
  clearDraft
}) {
  const searchHasValue = String(patientSearch || "").trim().length > 0

  const visiblePatients = useMemo(() => {
    return Array.isArray(patients) ? patients : []
  }, [patients])

  useEffect(() => {
    if (patientIdFromUrl) return
    if (!searchHasValue) return
    if (visiblePatients.length !== 1) return

    const onlyPatient = visiblePatients[0]
    const nextId = String(onlyPatient?.id || "")
    if (!nextId) return

    if (String(selectedPatientId || "") === nextId) return

    setSelectedPatientId(nextId)
    setSelectedPatientName(
      `${onlyPatient.firstName || ""} ${onlyPatient.lastName || ""}`.trim()
    )
  }, [
    patientIdFromUrl,
    searchHasValue,
    visiblePatients,
    selectedPatientId,
    setSelectedPatientId,
    setSelectedPatientName
  ])

  if (!createModeOpen) return null

  const activePatientLabel = selectedPatientName || "Sin seleccionar"

  const hasDetectedPrefill = Boolean(
    planHeader.title ||
      planHeader.notes ||
      itemDraft.tooth ||
      itemDraft.surface ||
      itemDraft.condition ||
      quickTreatmentForm.odontogramCondition
  )

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 999997,
        background: "rgba(15,23,42,0.24)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: 18,
        backdropFilter: "blur(2px)"
      }}
      onMouseDown={onClose}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          width: "min(1180px, 100%)",
          maxHeight: "92vh",
          overflow: "auto",
          background: "#f8fafc",
          borderRadius: 22,
          border: "1px solid #e2e8f0",
          boxShadow: "0 30px 80px rgba(15,23,42,0.22)",
          display: "grid",
          gap: 16,
          padding: 18
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 14,
            alignItems: "flex-start",
            flexWrap: "wrap"
          }}
        >
          <div>
            <div
              style={{
                display: "inline-flex",
                padding: "6px 10px",
                borderRadius: 999,
                background: "#eff6ff",
                border: "1px solid #bfdbfe",
                color: "#1d4ed8",
                fontSize: 10.5,
                fontWeight: 900,
                letterSpacing: 0.45,
                textTransform: "uppercase",
                marginBottom: 10
              }}
            >
              Nuevo plan manual
            </div>

            <div
              style={{
                fontSize: 28,
                fontWeight: 900,
                color: "#0f172a",
                letterSpacing: "-0.04em",
                lineHeight: 1.02
              }}
            >
              Crear plan de tratamiento
            </div>

            <div
              style={{
                color: "#64748b",
                marginTop: 6,
                fontSize: 13,
                lineHeight: 1.45,
                maxWidth: 760
              }}
            >
              Captura el plan en una capa clara sobre la pantalla, sin esconder el formulario debajo del listado.
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={clearDraft}
              style={{ ...buttonStyle, background: "#ffffff" }}
            >
              Limpiar borrador
            </button>

            <button
              type="button"
              onClick={onClose}
              style={{ ...buttonStyle, background: "#f8fafc" }}
            >
              Cerrar
            </button>
          </div>
        </div>

        {hasDetectedPrefill ? (
          <div
            style={{
              border: "1px solid #bfdbfe",
              background: "#eff6ff",
              color: "#1e3a8a",
              borderRadius: 16,
              padding: 14,
              lineHeight: 1.45
            }}
          >
            <div style={{ fontWeight: 900, marginBottom: 4 }}>
              Se detectó información precargada
            </div>
            <div style={{ fontSize: 13.5 }}>
              Puedes conservarla, editarla o limpiar el borrador si quieres empezar desde cero.
            </div>
          </div>
        ) : null}

        <SectionCard
          title="Contexto del plan"
          right={
            <div
              style={{
                padding: "6px 10px",
                borderRadius: 999,
                border: "1px solid #bfdbfe",
                background: "#eff6ff",
                color: "#1d4ed8",
                fontSize: 12,
                fontWeight: 800
              }}
            >
              Paciente activo: {activePatientLabel}
            </div>
          }
        >
          <div style={{ display: "grid", gap: 16 }}>
            {!patientIdFromUrl ? (
              <div
                style={{
                  border: "1px solid #e2e8f0",
                  borderRadius: 16,
                  padding: 14,
                  background: "#f8fafc",
                  display: "grid",
                  gap: 12
                }}
              >
                <div style={{ fontWeight: 800, color: "#0f172a" }}>
                  Seleccionar paciente
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                    gap: 12
                  }}
                >
                  <div>
                    <label style={labelStyle}>Buscar paciente</label>
                    <input
                      value={patientSearch}
                      onChange={(e) => setPatientSearch(e.target.value)}
                      placeholder="Nombre, teléfono o email"
                      style={inputStyle}
                    />
                    <div
                      style={{
                        marginTop: 6,
                        fontSize: 12,
                        color: "#64748b",
                        lineHeight: 1.35
                      }}
                    >
                      Se muestran hasta 20 resultados para mantenerlo rápido.
                    </div>
                  </div>

                  <div>
                    <label style={labelStyle}>Paciente</label>
                    <select
                      value={selectedPatientId}
                      onChange={(e) => {
                        const value = e.target.value
                        setSelectedPatientId(value)

                        const picked =
                          visiblePatients.find((item) => String(item.id) === String(value)) || null

                        setSelectedPatientName(
                          picked
                            ? `${picked.firstName || ""} ${picked.lastName || ""}`.trim()
                            : ""
                        )
                      }}
                      style={inputStyle}
                    >
                      <option value="">
                        {loadingPatients
                          ? "Cargando..."
                          : searchHasValue && visiblePatients.length === 1
                            ? "Autoseleccionado"
                            : "Selecciona"}
                      </option>
                      {visiblePatients.map((item) => (
                        <option key={item.id} value={item.id}>
                          {`${item.firstName || ""} ${item.lastName || ""}`.trim()}
                          {item.phone ? ` · ${item.phone}` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            ) : null}

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                gap: 12
              }}
            >
              <div>
                <label style={labelStyle}>Título del plan</label>
                <input
                  value={planHeader.title}
                  onChange={(e) =>
                    setPlanHeader((prev) => ({
                      ...prev,
                      title: e.target.value
                    }))
                  }
                  placeholder="Ej. Plan integral superior"
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Total borrador</label>
                <input
                  value={formatMoney(planDraftTotal)}
                  readOnly
                  style={{ ...inputStyle, background: "#f8fafc" }}
                />
              </div>
            </div>

            <div>
              <label style={labelStyle}>Notas generales del plan</label>
              <textarea
                value={planHeader.notes}
                onChange={(e) =>
                  setPlanHeader((prev) => ({
                    ...prev,
                    notes: e.target.value
                  }))
                }
                placeholder="Notas generales, alcance del plan, indicaciones..."
                style={textareaStyle}
              />
            </div>
          </div>
        </SectionCard>

        <SectionCard
          title="Agregar ítem al plan"
          right={
            <button
              type="button"
              onClick={() => setShowQuickTreatmentForm((prev) => !prev)}
              style={{ ...buttonStyle, background: "#ffffff" }}
            >
              {showQuickTreatmentForm ? "Cerrar alta rápida" : "Alta rápida de tratamiento"}
            </button>
          }
        >
          <div style={{ display: "grid", gap: 14 }}>
            {showQuickTreatmentForm ? (
              <div
                style={{
                  border: "1px solid #dbeafe",
                  background: "#ffffff",
                  borderRadius: 14,
                  padding: 14,
                  display: "grid",
                  gap: 12
                }}
              >
                <div style={{ fontWeight: 800, color: "#1d4ed8" }}>
                  Crear tratamiento sin salir del plan
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: 12
                  }}
                >
                  <div>
                    <label style={labelStyle}>Nombre</label>
                    <input
                      value={quickTreatmentForm.name}
                      onChange={(e) =>
                        setQuickTreatmentForm((prev) => ({
                          ...prev,
                          name: e.target.value
                        }))
                      }
                      style={inputStyle}
                    />
                  </div>

                  <div>
                    <label style={labelStyle}>Precio</label>
                    <input
                      type="number"
                      value={quickTreatmentForm.price}
                      onChange={(e) =>
                        setQuickTreatmentForm((prev) => ({
                          ...prev,
                          price: e.target.value
                        }))
                      }
                      style={inputStyle}
                    />
                  </div>

                  <div>
                    <label style={labelStyle}>Duración (min)</label>
                    <input
                      type="number"
                      value={quickTreatmentForm.durationMinutes}
                      onChange={(e) =>
                        setQuickTreatmentForm((prev) => ({
                          ...prev,
                          durationMinutes: e.target.value
                        }))
                      }
                      style={inputStyle}
                    />
                  </div>

                  <div>
                    <label style={labelStyle}>Especialidad requerida</label>
                    <input
                      value={quickTreatmentForm.requiredSpecialty}
                      onChange={(e) =>
                        setQuickTreatmentForm((prev) => ({
                          ...prev,
                          requiredSpecialty: e.target.value.toUpperCase()
                        }))
                      }
                      placeholder="Ej. GENERAL / ENDODONCIA"
                      style={inputStyle}
                    />
                  </div>

                  <div>
                    <label style={labelStyle}>Recall (días)</label>
                    <input
                      type="number"
                      value={quickTreatmentForm.recallDays}
                      onChange={(e) =>
                        setQuickTreatmentForm((prev) => ({
                          ...prev,
                          recallDays: e.target.value
                        }))
                      }
                      style={inputStyle}
                    />
                  </div>

                  <div>
                    <label style={labelStyle}>Condición odontograma</label>
                    <input
                      value={quickTreatmentForm.odontogramCondition}
                      onChange={(e) =>
                        setQuickTreatmentForm((prev) => ({
                          ...prev,
                          odontogramCondition: e.target.value.toUpperCase()
                        }))
                      }
                      placeholder="Ej. CARIES"
                      style={inputStyle}
                    />
                  </div>
                </div>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button
                    type="button"
                    onClick={handleQuickCreateTreatment}
                    disabled={savingQuickTreatment}
                    style={{
                      ...buttonStyle,
                      background: "#eff6ff",
                      color: "#1d4ed8",
                      border: "1px solid #bfdbfe",
                      opacity: savingQuickTreatment ? 0.7 : 1,
                      cursor: savingQuickTreatment ? "not-allowed" : "pointer"
                    }}
                  >
                    {savingQuickTreatment ? "Guardando..." : "Crear y usar tratamiento"}
                  </button>
                </div>
              </div>
            ) : null}

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                gap: 12
              }}
            >
              <div style={{ gridColumn: "span 2" }}>
                <label style={labelStyle}>Tratamiento</label>
                <select
                  value={itemDraft.treatmentId}
                  onChange={(e) =>
                    setItemDraft((prev) => ({
                      ...prev,
                      treatmentId: e.target.value
                    }))
                  }
                  style={inputStyle}
                >
                  <option value="">
                    {loadingTreatments ? "Cargando..." : "Selecciona"}
                  </option>
                  {treatmentsSorted.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name} · {formatMoney(item.price || 0)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>Pieza</label>
                <input
                  value={itemDraft.tooth}
                  onChange={(e) =>
                    setItemDraft((prev) => ({
                      ...prev,
                      tooth: e.target.value
                    }))
                  }
                  placeholder="Ej. 16"
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Superficie</label>
                <input
                  value={itemDraft.surface}
                  onChange={(e) =>
                    setItemDraft((prev) => ({
                      ...prev,
                      surface: e.target.value.toUpperCase()
                    }))
                  }
                  placeholder="O / M / D / V / L"
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Precio</label>
                <input
                  type="number"
                  value={itemDraft.price}
                  onChange={(e) =>
                    setItemDraft((prev) => ({
                      ...prev,
                      price: e.target.value
                    }))
                  }
                  placeholder="0"
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Condición clínica</label>
                <input
                  value={itemDraft.condition}
                  onChange={(e) =>
                    setItemDraft((prev) => ({
                      ...prev,
                      condition: e.target.value.toUpperCase()
                    }))
                  }
                  placeholder="Ej. CARIES"
                  style={inputStyle}
                />
              </div>

              <div style={{ gridColumn: "span 2" }}>
                <label style={labelStyle}>Notas del ítem</label>
                <input
                  value={itemDraft.notes}
                  onChange={(e) =>
                    setItemDraft((prev) => ({
                      ...prev,
                      notes: e.target.value
                    }))
                  }
                  placeholder="Indicaciones o detalle del ítem"
                  style={inputStyle}
                />
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={addDraftItem}
                style={{
                  ...buttonStyle,
                  background: "#eff6ff",
                  color: "#1d4ed8",
                  border: "1px solid #bfdbfe"
                }}
              >
                Agregar ítem
              </button>
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Ítems del plan">
          <div style={{ display: "grid", gap: 10 }}>
            {!planItems.length ? (
              <div
                style={{
                  color: "#64748b",
                  border: "1px dashed #e2e8f0",
                  borderRadius: 14,
                  padding: 14,
                  background: "#f8fafc"
                }}
              >
                Aún no agregas tratamientos al plan.
              </div>
            ) : (
              planItems.map((item, index) => (
                <TreatmentDraftCard
                  key={item.localId}
                  item={item}
                  index={index}
                  onRemove={() => removeDraftItem(item.localId)}
                  formatMoney={formatMoney}
                />
              ))
            )}
          </div>
        </SectionCard>

        <div
          style={{
            position: "sticky",
            bottom: 0,
            paddingTop: 8,
            background: "linear-gradient(180deg, rgba(248,250,252,0) 0%, #f8fafc 28%)",
            display: "flex",
            justifyContent: "space-between",
            gap: 10,
            flexWrap: "wrap"
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              ...buttonStyle,
              background: "#ffffff"
            }}
          >
            Cancelar
          </button>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={createManualPlan}
              disabled={creatingPlan}
              style={{
                ...buttonStyle,
                background: "#eff6ff",
                color: "#1d4ed8",
                border: "1px solid #bfdbfe",
                opacity: creatingPlan ? 0.7 : 1,
                cursor: creatingPlan ? "not-allowed" : "pointer"
              }}
            >
              {creatingPlan ? "Creando..." : "Crear plan"}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}